import { Box, Button, Menu, MenuItem, Text } from "@showitinc/showit-wc";
import React, { useRef } from "react";
import useStoreState from "@/components/providers/useStoreState";
import { isIOS } from "@/constants/BrowserConstants";
import PexelsSelectHandler from "@/components/utils/PexelsSelectHandler";
import useV2Feature from "@/hooks/useV2Feature";
import { canAddFormFields } from "@/stores/CommandPaletteStore";
import GroupStore from "@/stores/GroupStore";
import SiteDesignStore from "@/stores/SiteDesignStore";
import UIActions from "../../actions/UIActions";
import AppConstants from "../../constants/AppConstants";
import { FORMS_FLAG } from "../../constants/FeatureFlags.mjs";
import { FORM_FIELD_MENU_ITEMS, FORM_SUBMIT_MENU_ITEM } from "../../constants/FormFieldConstants.mjs";
import useMenuPopoverHover from "../../hooks/useMenuPopoverHover";
import {
  addTextElement,
  addElement,
  addButton,
  addOvalElement,
  addFormFieldElement,
  addSubmitButton,
} from "../../utils/elementCreationUtils";
import styles from "./Footer.module.css";

const {
  ELEMENT_TYPE_SOCIAL_GRID,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_LOTTIE,
  TEXT_STYLE_TITLE,
  TEXT_STYLE_HEADING,
  TEXT_STYLE_SUBHEADING,
  TEXT_STYLE_PARAGRAPH,
  ASSET_LIBRARY_TAB_FILES,
  ASSET_LIBRARY_TAB_STOCK,
} = AppConstants;

const CreateElementMenus = ({ blockContainer }) => {
  const shapeMenuRef = useRef(null);
  const HOVER_DELAY = 1000;

  const shapeMenuHover = useMenuPopoverHover(shapeMenuRef, HOVER_DELAY);
  const groupSelectionId = useStoreState(GroupStore, GroupStore.getGroupSelectionId);
  const isFormsEnabled = useV2Feature(FORMS_FLAG);
  // Like the stage, layers and palette menus: no form fields on a site canvas instance.
  const isFormTargetAllowed = useStoreState(SiteDesignStore, canAddFormFields);
  const isFormFieldsColumnEnabled = isFormsEnabled && isFormTargetAllowed;
  // External image keys require the v2 renderer.
  const stockPhotosEnabled = useV2Feature();

  /**
   * @param {string} [activeTab=ASSET_LIBRARY_TAB_FILES]
   */
  const _showAssetPanel = (activeTab = ASSET_LIBRARY_TAB_FILES) => {
    const mediaLibraryConfig = {
      title: "Add Elements",
      btnLabel: "Add to Page",
      activeTab,
      stockPhotoOnSelect: (photo) =>
        PexelsSelectHandler.createPhotoElement(photo, { containedBy: groupSelectionId }),
    };
    if (blockContainer.get("isBlock")) {
      mediaLibraryConfig.btnLabel = "Add To Site Canvas";
    }
    UIActions.toggleAssetChooser(mediaLibraryConfig);
  };

  return (
    <Box
      id="add-element-menu"
      d="flex"
      align="center"
      className={styles["toolbar-elements-wrapper"]}
      data-intercom-target="add elements"
    >
      <Menu
        ref={shapeMenuRef}
        shift="center"
        className={styles["toolbar-shape-elements-menu"]}
        // a hovering pencil would open it in passing; on iPad it opens on tap
        onMouseEnter={isIOS ? undefined : shapeMenuHover.handleMouseEnter}
        onMouseLeave={isIOS ? undefined : shapeMenuHover.handleMouseLeave}
      >
        <Button
          className={styles["toolbar-menu-button"]}
          variant="feature"
          data-testid="footer-add-element-button"
          icon-prefix="shapeElements"
          iconSize="20"
          icon-suffix="chevronDown"
          slot="trigger-element"
        >
          Add Element
        </Button>
        <Box d="flex">
          <Box
            className={`${styles["footer-elements-column"]}`}
            d="flex"
            flex="col"
            alignItems="start"
            pr="2"
          >
            <Box pb="2" pl="2">
              <Text variant="heading-2" variantStyle="strong" color="neutral">
                Text
              </Text>
            </Box>
            <MenuItem
              icon="textElement"
              variant="icon"
              iconSize="20"
              onClick={() => addTextElement(TEXT_STYLE_TITLE, { containedBy: groupSelectionId })}
            >
              Title
            </MenuItem>
            <MenuItem
              icon="textElement"
              variant="icon"
              iconSize="20"
              onClick={() => addTextElement(TEXT_STYLE_HEADING, { containedBy: groupSelectionId })}
            >
              Heading
            </MenuItem>
            <MenuItem
              icon="textElement"
              variant="icon"
              iconSize="20"
              onClick={() => addTextElement(TEXT_STYLE_SUBHEADING, { containedBy: groupSelectionId })}
            >
              Subheading
            </MenuItem>
            <MenuItem
              icon="textElement"
              data-testid="footer-add-text-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addTextElement(TEXT_STYLE_PARAGRAPH, { containedBy: groupSelectionId })}
            >
              Paragraph
            </MenuItem>
          </Box>
          <Box
            className={`${styles["footer-elements-column"]}`}
            d="flex"
            flex="col"
            alignItems="start"
            width="33%"
            px="2"
          >
            <Box pb="2" pl="2">
              <Text variant="heading-2" variantStyle="strong" color="neutral">
                Elements
              </Text>
            </Box>
            <MenuItem
              icon="lineElement"
              data-testid="footer-add-line-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_LINE, { containedBy: groupSelectionId })}
            >
              Line
            </MenuItem>
            <MenuItem
              icon="squareElement"
              data-testid="footer-add-rectangle-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_SIMPLE, { containedBy: groupSelectionId })}
            >
              Rectangle
            </MenuItem>
            <MenuItem
              icon="ellipse"
              data-testid="footer-add-oval-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addOvalElement({ containedBy: groupSelectionId })}
            >
              Oval
            </MenuItem>
            <MenuItem
              icon="symbolElement"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_ICON, { containedBy: groupSelectionId })}
            >
              Icon
            </MenuItem>
            <MenuItem
              icon="buttonElement"
              variant="icon"
              iconSize="20"
              onClick={() => addButton({ containedBy: groupSelectionId })}
            >
              Button
            </MenuItem>
            <MenuItem
              icon="mediaLibrary"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_GRAPHIC, { containedBy: groupSelectionId })}
            >
              Image
            </MenuItem>
            <MenuItem
              icon="galleryElement"
              data-testid="footer-add-gallery-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_GALLERY, { containedBy: groupSelectionId })}
            >
              Gallery
            </MenuItem>
            <MenuItem
              icon="videoElement"
              data-testid="footer-add-video-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_VIDEO, { containedBy: groupSelectionId })}
            >
              Video
            </MenuItem>
            <MenuItem
              icon="lottieFiles"
              data-testid="footer-add-lottie-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_LOTTIE, { containedBy: groupSelectionId })}
            >
              Lottie
            </MenuItem>
            <MenuItem
              icon="socialGridElement"
              data-testid="footer-add-social-grid-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_SOCIAL_GRID, { containedBy: groupSelectionId })}
            >
              Social Grid
            </MenuItem>
            <MenuItem
              icon="embedElement"
              data-testid="footer-add-embed-element-button"
              variant="icon"
              iconSize="20"
              onClick={() => addElement(ELEMENT_TYPE_IFRAME, { containedBy: groupSelectionId })}
            >
              Embed Code
            </MenuItem>
          </Box>
          {isFormFieldsColumnEnabled && (
            <Box
              className={`${styles["footer-elements-column"]}`}
              d="flex"
              flex="col"
              alignItems="start"
              px="2"
            >
              <Box pb="2" pl="2">
                <Text variant="heading-2" variantStyle="strong" color="neutral">
                  Form Fields
                </Text>
              </Box>
              {FORM_FIELD_MENU_ITEMS.map(({ id, icon, label, field }) => (
                <MenuItem
                  key={id}
                  icon={icon}
                  data-testid={`footer-add-form-field-${id}-button`}
                  variant="icon"
                  iconSize="20"
                  onClick={() => addFormFieldElement(field, { containedBy: groupSelectionId })}
                >
                  {label}
                </MenuItem>
              ))}
              <MenuItem
                icon={FORM_SUBMIT_MENU_ITEM.icon}
                data-testid="footer-add-form-field-submit-button"
                variant="icon"
                iconSize="20"
                onClick={() => addSubmitButton({ containedBy: groupSelectionId })}
              >
                {FORM_SUBMIT_MENU_ITEM.label}
              </MenuItem>
            </Box>
          )}
          <Box
            className={styles["footer-elements-column"]}
            d="flex"
            flex="col"
            alignItems="start"
            pl="2"
          >
            <Box pb="2" pl="2">
              <Text variant="heading-2" variantStyle="strong" color="neutral">
                Media
              </Text>
            </Box>

            <MenuItem
              icon="addMediaLibrary"
              data-testid="footer-add-media-library-button"
              variant="icon"
              iconSize="20"
              onClick={() => _showAssetPanel()}
            >
              Open Media Library
            </MenuItem>
            {stockPhotosEnabled && (
              <MenuItem
                icon="search"
                data-testid="footer-add-stock-photo-button"
                variant="icon"
                iconSize="20"
                onClick={() => _showAssetPanel(ASSET_LIBRARY_TAB_STOCK)}
              >
                Stock Photos
              </MenuItem>
            )}
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default CreateElementMenus;
